import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { chamarApi } from '@/nucleo/auth/cliente-api';
import { naoInsistirEmRecusa } from '@/nucleo/naoInsistirEmRecusa';

import type { AlertaDeVencimento, TipoDeAlerta } from './contrato';

export const CAMINHO_DOS_VENCIMENTOS = '/bff/v1/portal-credenciado/vencimentos';

/** Um documento que vence em até 60 dias, ou já venceu, como a API devolve (0277). */
export interface VencimentoDaApi {
  documentoId: string;
  recursoTipo: 'VEICULO' | 'CONDUTOR' | 'MONITOR';
  recursoId: string;
  /** Placa do veículo; nome do condutor ou do monitor. */
  identificacao: string;
  tipoDocumento: string;
  titulo: string | null;
  validade: string;
  diasRestantes: number;
}

const TIPO_DO_ALERTA: Record<VencimentoDaApi['recursoTipo'], TipoDeAlerta> = {
  VEICULO: 'Veículo',
  CONDUTOR: 'Condutor',
  MONITOR: 'Monitor',
};

/**
 * Para onde o alerta leva, como no protótipo (`PortalDashboard.tsx`): o
 * detalhe do veículo ou do monitor; o condutor, que não tem detalhe próprio
 * no portal de todo credenciado, leva a Solicitações, onde se pede a troca.
 */
function rotaDoAlerta(v: VencimentoDaApi): string {
  if (v.recursoTipo === 'VEICULO') return `/credenciado/veiculos/${v.recursoId}`;
  if (v.recursoTipo === 'MONITOR') return `/credenciado/monitores/${v.recursoId}`;
  return '/credenciado/solicitacoes';
}

export function paraOAlerta(v: VencimentoDaApi): AlertaDeVencimento {
  const documento = v.titulo?.trim() || v.tipoDocumento;
  return {
    id: v.documentoId,
    tipo: TIPO_DO_ALERTA[v.recursoTipo],
    descricao: `${documento} — ${v.identificacao}`,
    validade: v.validade,
    dias_restantes: v.diasRestantes,
    rota: rotaDoAlerta(v),
  };
}

/**
 * Os documentos dos veículos, condutores e monitores do cadastro que vencem
 * em até 60 dias, ou já venceram, do mais urgente para o menos. Quem decide é
 * o servidor (`portal_credenciado_vencimentos`, migração 0277); sem cadastro
 * vinculado, a lista é vazia.
 */
export function useAlertasDeVencimento(): UseQueryResult<AlertaDeVencimento[], Error> {
  return useQuery<AlertaDeVencimento[], Error>({
    queryKey: ['credenciado', 'inicio', 'vencimentos'] as const,
    queryFn: async () =>
      (await chamarApi<{ itens: VencimentoDaApi[] }>(CAMINHO_DOS_VENCIMENTOS)).itens.map(
        paraOAlerta,
      ),
    staleTime: 5 * 60_000,
    retry: naoInsistirEmRecusa,
  });
}
